import React, { useState, useEffect, useRef, Fragment } from "react";
import { UserAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { Button } from "components/buttons";
import { FormTextInput } from "components/form-fields";
import { ListingTable } from "components/listing";
import { Tooltip } from "components/tooltip";
import { StringToEllipsesString } from "components/formatting";
import { ShowHide } from "components/layout";
import { NotificationToaster } from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { useDebounce } from "hooks/useDebounce";
import { PcnService } from "services/PcnService";
import { PcnIssuingAuthorityModal } from "pages/pcn/admin/settings/PcnIssuingAuthorityModal";

const requiredAction = "Internal";
const pageSize = 50;
const debounceTimeout = 750;
const badRequestCode = 400;
const generalErrorMessage = "An error occurred while loading issuing authorities. Please try again.";

const defaultModel = {
    id: 0,
    issuingAuthorityCode: "",
    name: "",
    appealWebsite: "",
    paymentWebsite: "",
    detailsWebsite: "",
    homeWebsite: ""
};

const sortableHeaders = [
    { headerName: "Issuing Authority Code", sortName: "ISSUINGAUTHORITYCODE" },
    { headerName: "Name", sortName: "NAME" }
];

const tableHeaders = ["Issuing Authority Code", "Name", "Appeal Website", "Payment Website", "Details Website", "Home Website", "Actions"];

export function PcnIssuingAuthoritiesTab() {
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const [loading, setLoading] = useState(true);
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [sortedBy, setSortedBy] = useState(sortableHeaders[0]);
    const [sortedDir, setSortedDir] = useState("A");

    const [showModal, setShowModal] = useState(false);
    const [isModalUpdate, setIsModalUpdate] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedAuthority, setSelectedAuthority] = useState(defaultModel);

    const inputSearchRef = useRef(null);

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
        setPageNumber(1);
    }

    function onSort(header, direction) {
        setSortedBy(header);
        setSortedDir(direction);
        setPageNumber(1);
    }

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function addIssuingAuthority() {
        setIsModalUpdate(false);
        setSelectedAuthority(defaultModel);
        setShowModal(true);
    }

    function onEditIssuingAuthority(id) {
        setModalLoading(true);
        setIsModalUpdate(true);
        setShowModal(true);

        PcnService.getIssuingAuthority(id)
            .then((details) => {
                setSelectedAuthority(details);
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not load the issuing authority. Please try again.");
                onCloseModal();
            })
            .finally(() => {
                setModalLoading(false);
            });
    }

    function onCloseModal() {
        setShowModal(false);
        setSelectedAuthority(defaultModel);
        setIsModalUpdate(false);
    }

    function onSaveModal() {
        onCloseModal();
        fetchData();
    }

    function fetchData() {
        setLoading(true);

        const searchRequest = {
            requestCount: pageSize,
            pageNumber: pageNumber,
            searchTerm: debouncedSearchTerm,
            sortField: sortedBy.sortName,
            sortDirection: sortedDir
        };

        PcnService.searchIssuingAuthorities(searchRequest)
            .then((response) => {
                const rows = (response.data ?? []).map((row) => {
                    return [
                        row.issuingAuthorityCode,
                        row.name,
                        <StringToEllipsesString key={`${row.id}-appealWebsite`} length={30} showTooltip>{row.appealWebsite || ""}</StringToEllipsesString>,
                        <StringToEllipsesString key={`${row.id}-paymentWebsite`} length={30} showTooltip>{row.paymentWebsite || ""}</StringToEllipsesString>,
                        <StringToEllipsesString key={`${row.id}-detailsWebsite`} length={30} showTooltip>{row.detailsWebsite || ""}</StringToEllipsesString>,
                        <StringToEllipsesString key={`${row.id}-homeWebsite`} length={30} showTooltip>{row.homeWebsite || ""}</StringToEllipsesString>,
                        <div key={`${row.id}-actions`} className="button-row-small inline-items">
                            <Tooltip content="Edit issuing authority">
                                <Button
                                    onClick={() => onEditIssuingAuthority(row.id)}
                                    icon="edit"
                                    iconOnly={true}
                                    minimal={true}
                                    large={false}
                                />
                            </Tooltip>
                        </div>
                    ];
                });

                setTableRows(rows);
                setTotalRecords(response.totalCount);
            }, (error) => {
                if (error?.response?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.response.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, generalErrorMessage);
                }
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, generalErrorMessage);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        fetchData();
    }, [debouncedSearchTerm, pageNumber, sortedBy, sortedDir]);

    return (
        <UserAccess
            perform={requiredAction}
            yes={() => (
                <Fragment>
                    <div className="spacer-bottom">
                        <Button intent="primary" text="Add new issuing authority" id="add-issuing-authority" onClick={addIssuingAuthority} loading={loading} />
                    </div>

                    <FormTextInput
                        inputRef={inputSearchRef}
                        placeholder="Search issuing authorities"
                        disabled={loading && inputSearchRef.current !== document?.activeElement}
                        onChange={onSearchChange}
                        value={searchTerm}
                        large
                        icon="search"
                        id="issuing-authority-search-field"
                        fill
                    />

                    <ListingTable
                        id="listing-table-issuing-authorities"
                        headers={tableHeaders}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        loadingData={loading}
                        onPagingChange={onPagingChange}
                        startPage={pageNumber}
                        pageSize={pageSize}
                        pageNumber={pageNumber}
                        showPerPage={false}
                        sortable
                        sortableHeaders={sortableHeaders}
                        sortedBy={sortedBy}
                        sortedDir={sortedDir}
                        onSort={onSort}
                        noDataMessage="No issuing authorities to display"
                    />

                    <ShowHide
                        evaluator={showModal}
                        show={(
                            <PcnIssuingAuthorityModal
                                showModal={showModal}
                                modalTitle={isModalUpdate ? "Update Issuing Authority" : "Add New Issuing Authority"}
                                isUpdate={isModalUpdate}
                                loading={modalLoading}
                                onSave={onSaveModal}
                                onCloseModal={onCloseModal}
                                issuingAuthorityDetails={selectedAuthority}
                            />
                        )}
                    />
                </Fragment>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}
